import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

// base.css first (ported prototype styles + tokens); redesign.css overrides it
// with the "editorial obsidian" skin; tailwind layers last.
import './styles/base.css';
import './styles/mywork.css';
import './styles/redesign.css';
import './styles/tailwind.css';

import { AuthProvider } from './auth/AuthContext';
import { queryClient } from './lib/queryClient';
import { installGlobalErrorLogging } from './integrations/errorLog';
import { installAuthFetch } from './lib/authFetch';
import { router } from './router';
import { CrashGuard } from './components/CrashGuard';
import { TipLayer } from './components/TipLayer';

/* ONE address. The portal answers on its Vercel names too, and a session is
   stored per address — so a person who opens an old bookmark on
   agent-portal-lexidy.vercel.app finds themselves "signed out", asks for a
   new password, and the next day the other bookmark does it again. Every
   production name lands on agent-portal.lexidy.dev before anything renders.
   Preview builds keep their own names. */
const CANONICAL = 'agent-portal.lexidy.dev';
const OLD_NAMES = ['agent-portal-lexidy.vercel.app', 'agent-portal-git-main-lexidy-tech.vercel.app'];
if (OLD_NAMES.includes(window.location.hostname)) {
  window.location.replace(`https://${CANONICAL}${window.location.pathname}${window.location.search}${window.location.hash}`);
}

installAuthFetch();

// Log uncaught crashes / unhandled rejections to the portal fault log.
installGlobalErrorLogging();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* A render crash must never leave a blank page behind. */}
        <CrashGuard>
          <RouterProvider router={router} />
        </CrashGuard>
        {/* Every hover text in the portal, drawn once, above everything. */}
        <TipLayer />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
